import { XIcon } from "lucide-react";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { OfferInfo, SingleOffer } from "../types";
import { Avatar } from "@mantine/core";
import { Label, textVariants } from "@/src/components/Text/Text";
import { cn } from "@/lib/utils";
import AppendInput from "@/src/components/PhoneInput/AppendInput";
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/src/components/shad/select";
import { Button } from "@/src/components/Button/Button";
import { z } from "zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import api from "@/src/utils";
import apiRoutes from "@/src/apiRoutes";
import { useMutation, useQuery } from "@tanstack/react-query";
import useDebounce from "@/src/hooks/useDebounce";
import Tabs from "@/src/components/Tabs/Tabs";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/src/components/shad/select";
import InputComponent from "@/src/components/Input/Input";
import { useTranslation } from "react-i18next";

const formState = z.object({
  fiat_amount: z.number().optional(),
  crypto_amount: z.number().optional(),
  payment_id: z.string().optional(),
  account_name: z.string().optional(),
  account_number: z.string().optional(),
  extra: z.string().optional(),
});

interface Props {
  setView: Dispatch<SetStateAction<boolean>>;
  offer: OfferInfo | undefined;
}

const PreviewOffer = ({ setView, offer }: Props) => {
  const { t } = useTranslation();
  const trans = (key: string) => t(`app.p2p.preview.${key}`);
  type formType = z.infer<typeof formState>;
  const navigate = useNavigate();
  const [tab, setTab] = useState("fiat");
  const { register, handleSubmit, watch, setValue } = useForm<formType>({
    resolver: zodResolver(formState),
  });

  const debAmount = useDebounce(
    tab === "fiat" ? watch("fiat_amount") : watch("crypto_amount"),
    1500
  );
  const debAccountName = useDebounce(watch("account_name"), 1500);
  const debAccountNumber = useDebounce(watch("account_number"), 1500);
  const debPaymentId = useDebounce(watch("payment_id"), 1500);

  const getOffer = async () => {
    const res = await api.post(apiRoutes.offers, {
      uuid: offer?.uuid,
      type: "detail",
    });
    return res.data;
  };

  const { data: singleOffer } = useQuery<SingleOffer>({
    queryKey: ["offer", offer?.uuid],
    queryFn: getOffer,
  });

  const proceedPrev = async () => {
    const res = await api.post(apiRoutes.offers, {
      uuid: offer?.uuid,
      amount: tab === "fiat" ? watch("fiat_amount") : watch("crypto_amount"),
      amount_type: tab,
      type: "preview",
      payment_method:
        offer?.offer_type === "buy"
          ? {
              id: watch("payment_id"),
              account_name: watch("account_name"),
              account_number: watch("account_number"),
              extra: watch("extra"),
            }
          : offer?.payment_details.find(
              (item) => item.id.toString() === watch("payment_id")
            ),
    });
    return res.data;
  };

  const {
    mutate,
    data,
    isLoading: mutateLoad,
  } = useMutation({
    mutationFn: proceedPrev,
    onSuccess: (data) => {
      if (!data?.success) {
        toast.warning(data?.info);
      }
    },
  });

  const initTrade = async () => {
    const res = await api.post(apiRoutes.offers, {
      type: "initiate_trade",
      uuid: offer?.uuid,
    });
    return res.data;
  };

  const { mutate: mutateInit, isLoading } = useMutation({
    mutationFn: initTrade,
    onSuccess: (data) => {
      if (data?.success) {
        toast.success("Trade initiated");
        navigate(`/app/trade/ad/${data?.info}`);
      }
    },
  });

  const submit: SubmitHandler<formType> = () => {
    mutate();
  };

  const setMax = () => {
    if (tab === "fiat") {
      setValue("fiat_amount", Number(offer?.max_order_price));
    } else {
      setValue("crypto_amount", Number(singleOffer?.info.bounds.crypto.max));
    }
  };

  useEffect(
    function fetchPrev() {
      if (offer?.offer_type === "buy") {
        if (
          (watch("fiat_amount") || watch("fiat_amount")) &&
          watch("account_number") &&
          watch("account_name")
        ) {
          mutate();
        }
      } else {
        if (
          (watch("fiat_amount") || watch("fiat_amount")) &&
          watch("payment_id")
        ) {
          mutate();
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debAmount, debAccountName, debAccountNumber, debPaymentId]
  );

  return (
    <div className="grid md:grid-cols-2 ">
      <div className="bg-[#F6F8FF] dark:bg-grey-500 p-4 divide-y divide-gray-200 dark:divide-grey-400">
        <div className="flex gap-2 pb-3">
          <div>
            <Avatar size={"sm"} color="blue" radius={"xl"}>
              {offer?.trader__username[0]}{" "}
            </Avatar>
          </div>
          <div className="grid">
            <span className={textVariants({ size: "sm", case: "capitalize" })}>
              {" "}
              {offer?.trader__username}
            </span>
            <span
              className={textVariants({
                size: "xs",
                variant: "desc",
                font: "semibold",
              })}
            >
              {offer?.transactions} {trans("transactions")}
            </span>
            <span className="flex items-center gap-2">
              <span
                className={cn(
                  "p-1 rounded-full",
                  offer?.online_status.online ? "bg-green-500" : "bg-red-500"
                )}
              ></span>
              <span
                className={textVariants({
                  size: "xs",
                  variant: "desc",
                  font: "semibold",
                })}
              >
                {offer?.online_status.online
                  ? trans("online")
                  : trans("offline")}
              </span>
            </span>
          </div>
        </div>
        <div className="pt-3 grid gap-3">
          <div className={textVariants({ variant: "desc", size: "sm" })}>
            {trans("details.price")}:{" "}
            <span className={textVariants({ size: "sm" })}>
              {singleOffer?.info.unit_price} {singleOffer?.info.currency}
            </span>
          </div>
          <div className={textVariants({ variant: "desc", size: "sm" })}>
            {trans("details.quantity")}:{" "}
            <span className={textVariants({ size: "sm" })}>
              {singleOffer?.info.trading_amount}{" "}
              <span className={textVariants({ size: "sm", case: "uppercase" })}>
                {singleOffer?.info.currency}
              </span>
            </span>
          </div>
          <div className={textVariants({ variant: "desc", size: "sm" })}>
            {trans("details.limits")}:{" "}
            {tab === "fiat" ? (
              <span className={textVariants({ size: "sm" })}>
                {singleOffer?.info.bounds.fiat.min} -{" "}
                {singleOffer?.info.bounds.fiat.max} {offer?.currency}
              </span>
            ) : (
              <span className={textVariants({ size: "sm" })}>
                {singleOffer?.info.bounds.crypto.min} -{" "}
                {singleOffer?.info.bounds.crypto.max}{" "}
                <span className="uppercase">{offer?.coin__ticker}</span>
              </span>
            )}
          </div>
          <div
            className={cn(
              textVariants({ variant: "desc", size: "sm" }),
              "flex gap-2"
            )}
          >
            {trans("details.paymentMethod")}:
            <div className="hidden md:grid gap-3">
              {singleOffer?.info.payment_method.map((item) => (
                <div
                  className="flex whitespace-nowrap items-center gap-1"
                  key={item?.id}
                >
                  <img
                    src={item?.image}
                    alt={item?.name}
                    className="w-5 h-5 rounded-full"
                  />
                  <span className="pl-1">{item?.name}</span>{" "}
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap md:hidden items-center gap-3 ">
            {singleOffer?.info.payment_method.map((item) => (
              <div
                className="flex whitespace-nowrap items-center gap-1"
                key={item?.id}
              >
                <img
                  src={item?.image}
                  alt={item?.name}
                  className="w-5 h-5 rounded-full"
                />
                <span className="pl-1">{item?.name}</span>{" "}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="md:p-4">
        <div className="md:flex hidden justify-end">
          <XIcon
            onClick={() => setView(false)}
            className="h-4 w-4 dark:text-gray-200 cursor-pointer"
          />
        </div>
        <Tabs
          tabs={[
            { name: trans("form.tabs.byFiat"), value: "fiat" },
            { name: trans("form.tabs.byCrypto"), value: "crypto" },
          ]}
          activeTab={tab}
          setTab={setTab}
          space="2"
        />
        <form onSubmit={handleSubmit(submit)} className="grid gap-3 py-6">
          <AppendInput
            {...register(tab === "fiat" ? "fiat_amount" : "crypto_amount", {
              valueAsNumber: true,
            })}
            type={"number"}
            pattern="[0-9]*"
            min={
              tab === "fiat"
                ? Number(offer?.min_order_price)
                : Number(singleOffer?.info.bounds.crypto.min)
            }
            max={Number(offer?.max_order_price)}
            placeholder="Amount"
            right={
              <div className="p-2 flex items-center space-x-3">
                <span
                  className={textVariants({
                    case: "uppercase",
                    variant: "desc",
                  })}
                >
                  {tab === "fiat"
                    ? singleOffer?.info.currency
                    : singleOffer?.info.coin__ticker}
                </span>

                <button
                  onClick={setMax}
                  className={textVariants({ variant: "link", font: "light" })}
                >
                  {trans("form.max")}
                </button>
              </div>
            }
          />
          <Select onValueChange={(val) => setValue("payment_id", val)}>
            <SelectTrigger className="w-full font-semibold">
              <SelectValue
                placeholder={trans("form.paymentMethod.placeholder")}
              />
            </SelectTrigger>
            <SelectContent>
              {offer?.payment_method.map((item) => (
                <SelectItem value={item?.id.toString()} key={item?.id}>
                  <span className="pl-1">{item?.name}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {offer?.offer_type === "buy" && watch("payment_id") && (
            <div className="grid gap-2">
              <InputComponent
                {...register("account_name")}
                label={trans("form.accountName.label")}
                placeholder={trans("form.accountName.placeholder")}
                className="placeholder:text-gray-400"
              />
              <InputComponent
                {...register("account_number")}
                label={trans("form.accountNumber.label")}
                placeholder={trans("form.accountNumber.placeholder")}
                className="placeholder:text-gray-400"
              />
              <div className="grid">
                <Label>{trans("form.extra.label")}</Label>
                <textarea
                  {...register("extra")}
                  rows={3}
                  className="focus:outline-indigo-400 border border-gray-200 rounded-md dark:focus:outline-grey-400 focus:outline-none dark:bg-grey-600 bg-input-field  dark:text-white dark:placeholder:text-gray-200"
                />
              </div>
            </div>
          )}
          {(data?.info || mutateLoad) && (
            <div className="grid gap-2">
              <div className="flex justify-between items-center">
                <span className={textVariants({ variant: "desc", size: "sm" })}>
                  {trans("form.youWillPay")}
                </span>
                {!mutateLoad ? (
                  <span className={textVariants()}>
                    {data?.info?.amount_local} {offer?.currency}
                  </span>
                ) : (
                  <span className={textVariants()}>{trans("form.wait")}</span>
                )}
              </div>
              <div className="flex justify-between items-center">
                <span className={textVariants({ variant: "desc", size: "sm" })}>
                  {trans("form.youWillReceive")}
                </span>
                {!mutateLoad ? (
                  <span className={textVariants({ case: "uppercase" })}>
                    {offer?.offer_type === "buy"
                      ? data?.info?.amount_to_deposit_crypto
                      : data?.info?.amount_to_withdraw_crypto}{" "}
                    {offer?.coin__ticker}
                  </span>
                ) : (
                  <span className={textVariants()}>{trans("form.wait")}</span>
                )}
              </div>
            </div>
          )}
          <div className="mt-10">
            <div className="flex gap-1 mb-2">
              <input type="checkbox" />
              <span className={textVariants({ size: "xs", variant: "desc" })}>
                {trans("form.terms1")}{" "}
                <Link
                  to={"https://www.mybitstore.com/privacy"}
                  target="_blank"
                  className={cn(
                    textVariants({ variant: "link", size: "xs" }),
                    "cursor-pointer"
                  )}
                >
                  {" "}
                  {trans("form.terms2")}
                </Link>
              </span>
            </div>
            <Button
              onClick={() => mutateInit()}
              loading={isLoading}
              disabled={!data?.success}
              type="submit"
              className="w-full"
            >
              {singleOffer?.info.offer_type === "sell"
                ? trans("form.buy")
                : trans("form.sell")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PreviewOffer;
